import styled from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';
import RequestCard from './RequestCard';
import SubscribeCard from './SubscriptionCard';

const cardVariants = {
  hidden: { opacity: 0, y: 60, scale: 0.95 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: i * 0.2,
      duration: 0.8,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  }),
  hover: {
    y: -12,
    scale: 1.02,
    rotateX: 5,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
};

const bubbleFloat = {
  y: [0, -14, 0],
  x: [0, 1, -3, 0],
  rotate: [0, 0.7, -0.8, 0],
  transition: {
    duration: 6.5,
    ease: 'easeInOut' as const,
    repeat: Infinity,
    repeatType: 'reverse' as const,
  },
};

const Steps = styled.div`
  width: 100%;
  max-width: 1560px;
  display: grid;
  gap: 3rem;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const StepCardWrapper = styled(motion.div)`
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 520px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
  cursor: default;
  transform-style: preserve-3d;
  will-change: transform, opacity;
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  contain: layout paint style;

  &:hover {
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  }
`;

const ReceiveCard = styled(StepCardWrapper)`
  background: linear-gradient(135deg, #ff6a00 0%, #ff345b 50%, #ff0088 100%);
`;

const FloatLayer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  will-change: transform;
`;

const CardContent = styled.div`
  padding: 1.75rem 2rem 2rem;
  margin-top: auto;
  color: #fff;
`;

const StepTitle = styled.h3`
  font-size: 2rem;
  margin: 0 0 1rem;
  font-weight: 600;
`;

const StepDescription = styled.p`
  font-size: 1.125rem;
  margin: 0;
  line-height: 1.5;
`;

const CardTop = styled.div`
  padding: 1.75rem 2rem 1rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CardStack = styled.div`
  position: relative;
  width: 100%;
  height: 230px;
  margin-top: 1rem;
  display: flex;
  justify-content: center;
`;

const DesignCard = styled(motion.div)`
  position: absolute;
  width: 190px;
  height: 230px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  overflow: hidden;
`;

const CardHeader = styled.div<{ $bgColor: string }>`
  height: 60px;
  background: ${({ $bgColor }) => $bgColor};
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 8px;
`;

const CardAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
`;

const CardLine = styled.div`
  flex: 1;
  height: 4px;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 2px;
`;

const ReceiveCardContent = styled.div`
  padding: 16px;
  height: calc(100% - 60px);
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ContentBlock = styled.div<{ $height: string; $bgColor?: string }>`
  height: ${({ $height }) => $height};
  background: ${({ $bgColor }) => $bgColor || '#f0f0f0'};
  border-radius: 8px;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  height: 80px;
`;

const ProductItem = styled.div`
  background: #f8f8f8;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
`;

type ProcessCardsRowProps = {
  className?: string;
  hideSubscribePrice?: boolean;
};

export default function ProcessCardsRow({ className, hideSubscribePrice = false }: ProcessCardsRowProps) {
  const reduceMotion = useReducedMotion();

  return (
    <Steps className={className}>
      <SubscribeCard customIndex={0} hidePrice={hideSubscribePrice} />
      <RequestCard customIndex={1} />

      <ReceiveCard
        custom={2}
        variants={cardVariants}
        initial="hidden"
        whileInView="visible"
        whileHover="hover"
        viewport={{ once: true, amount: 0.2 }}
      >
        <FloatLayer animate={reduceMotion ? undefined : bubbleFloat}>
          <CardTop>
            <CardStack>
              <DesignCard
                style={{ top: '10px', left: '10%', x: '-50%', rotate: -8, zIndex: 3 }}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                animate={{
                  y: [0, -6, 0],
                  rotate: [-8, -6, -8],
                  x: [0, 2, 0],
                  transition: {
                    duration: 6,
                    ease: 'easeInOut' as const,
                    repeat: Infinity,
                    repeatType: 'reverse' as const,
                  },
                }}
                viewport={{ once: true }}
              >
                <CardHeader $bgColor="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
                  <CardAvatar />
                  <CardLine />
                </CardHeader>
                <ReceiveCardContent>
                  <ContentBlock
                    $height="120px"
                    $bgColor="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                  />
                  <ContentBlock $height="16px" />
                  <ContentBlock $height="12px" />
                  <ContentBlock $height="12px" />
                </ReceiveCardContent>
              </DesignCard>

              <DesignCard
                style={{ top: '25px', left: '20%', x: '-35%', rotate: 3, zIndex: 2 }}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                animate={{
                  y: [0, -8, 0],
                  rotate: [3, 5, 3],
                  x: [0, -1, 0],
                  transition: {
                    duration: 7,
                    ease: 'easeInOut' as const,
                    repeat: Infinity,
                    repeatType: 'reverse' as const,
                  },
                }}
                viewport={{ once: true }}
              >
                <CardHeader $bgColor="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)">
                  <CardLine />
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      background: 'rgba(255,255,255,0.4)',
                      borderRadius: '4px',
                    }}
                  />
                </CardHeader>
                <ReceiveCardContent>
                  <ProductGrid>
                    <ProductItem />
                    <ProductItem />
                    <ProductItem />
                    <ProductItem />
                  </ProductGrid>
                  <ContentBlock $height="16px" />
                  <ContentBlock $height="20px" $bgColor="#4facfe" />
                </ReceiveCardContent>
              </DesignCard>

              <DesignCard
                style={{ top: '40px', left: '30%', x: '-65%', rotate: 12, zIndex: 1 }}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.6 }}
                animate={{
                  y: [0, -10, 0],
                  rotate: [12, 14, 12],
                  x: [0, -2, 0],
                  transition: {
                    duration: 8,
                    ease: 'easeInOut' as const,
                    repeat: Infinity,
                    repeatType: 'reverse' as const,
                  },
                }}
                viewport={{ once: true }}
              >
                <CardHeader $bgColor="linear-gradient(135deg, #fa709a 0%, #fee140 100%)">
                  <div
                    style={{
                      width: '40px',
                      height: '6px',
                      background: 'rgba(255,255,255,0.4)',
                      borderRadius: '3px',
                    }}
                  />
                  <div
                    style={{
                      width: '16px',
                      height: '16px',
                      background: 'rgba(255,255,255,0.3)',
                      borderRadius: '50%',
                    }}
                  />
                </CardHeader>
                <ReceiveCardContent>
                  <ContentBlock
                    $height="40px"
                    $bgColor="linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)"
                  />
                  <ContentBlock $height="12px" />
                  <ContentBlock $height="12px" />
                  <ContentBlock $height="12px" />
                  <ContentBlock $height="60px" $bgColor="#fed6e3" />
                </ReceiveCardContent>
              </DesignCard>
            </CardStack>
          </CardTop>

          <CardContent>
            <StepTitle>Receive</StepTitle>
            <StepDescription>
              Receive your design within a few business days on average.
            </StepDescription>
          </CardContent>
        </FloatLayer>
      </ReceiveCard>
    </Steps>
  );
}
